import { ApiInstance, RetrySettings } from '@src/services/api-instance';
import { getAuthToken } from '@src/services/auth-service';
import { GetPaginatedScriptsDTO } from '../dto/brain-scripts.get.response.dto';
import { ScriptsDTO } from '../dto/brain-scripts.getById.response.dto';

import axios from 'axios';
import {
  ActionDTO,
  GetActionResponseDTO,
  GetActionsParamsDTO,
  GetActionsSummaryResponseDTO,
  ShortExecutionResponseDTO,
  ReportDTO,
  GetActionLayersResponseDTO,
} from '../dto/tools-requests.dto';
import { ToolsDocumentsService } from './documents/api-documents';

export class ToolsRequestsService {
  private readonly client: ApiInstance;
  public documents: ToolsDocumentsService;

  constructor(client: ApiInstance) {
    this.client = new ApiInstance(`${client.getBaseUrl()}/requests`, client.getTimeout());
    this.documents = new ToolsDocumentsService(this.client);
  }

  async get(request: {
    params: {
      page?: number;
      limit?: number;
      status?: ('PENDING' | 'RUNNING' | 'BLOCKED' | 'FINISHED' | 'FAILED')[];
      createdByUserId?: string;
      approvedByUserId?: string;
      organizationId?: string;
      startDate?: string;
      endDate?: string;
      name?: string;
      isApproved?: boolean;
    };
    settings?: RetrySettings;
  }): Promise<GetPaginatedScriptsDTO> {
    return (
      await this.client.get<GetPaginatedScriptsDTO>(``, request.params, {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      })
    ).data;
  }

  async getById(request: { id: string; settings?: RetrySettings }): Promise<ScriptsDTO> {
    return (
      await this.client.get<ScriptsDTO>(
        `${request.id}`,
        {},
        {
          bearerToken: await getAuthToken(),
          settings: request.settings,
        },
      )
    ).data;
  }

  async put(request: {
    id: string;
    body: { sleepTimeMs?: number; status?: string };
    settings?: RetrySettings;
  }): Promise<ShortExecutionResponseDTO> {
    return (
      await this.client.put<{ sleepTimeMs?: number; status?: string }, ShortExecutionResponseDTO>(
        `${request.id}`,
        {},
        request.body,
        {
          bearerToken: await getAuthToken(),
          settings: request.settings,
        },
      )
    ).data;
  }

  async postApprove(request: { id: string; settings?: RetrySettings }): Promise<void> {
    await this.client.post<object, void>(
      `${request.id}/approve`,
      {},
      {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      },
    );
  }

  async postRun(request: { id: string; settings?: RetrySettings }): Promise<void> {
    await this.client.post<object, void>(
      `${request.id}/run`,
      {},
      {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      },
    );
  }

  async postCancel(request: { id: string; settings?: RetrySettings }): Promise<void> {
    await this.client.post<object, void>(
      `${request.id}/cancel`,
      {},
      {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      },
    );
  }

  async postUncancel(request: { id: string; settings?: RetrySettings }): Promise<void> {
    await this.client.post<object, void>(
      `${request.id}/uncancel`,
      {},
      {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      },
    );
  }

  async export(request: {
    id: string;
    params: {
      format: 'csv' | 'xlsx';
      resolveResponse: boolean;
    };
    settings?: RetrySettings;
  }): Promise<Blob> {
    const mimeTypes = {
      csv: 'text/csv',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    };

    const response = await axios.get(`${this.client.getBaseUrl()}/${request.id}/export`, {
      headers: {
        Authorization: `Bearer ${await getAuthToken()}`,
        Accept: mimeTypes[request.params.format],
      },
      params: request.params,
      responseType: 'blob',
    });

    return response.data;
  }

  async getActionsByRequestId(request: {
    id: string;
    params: GetActionsParamsDTO;
    settings?: RetrySettings;
  }): Promise<GetActionResponseDTO> {
    return (
      await this.client.get<GetActionResponseDTO>(`${request.id}/actions`, request.params, {
        bearerToken: await getAuthToken(),
        settings: request.settings,
      })
    ).data;
  }

  async getActionsSummaryByRequestId(request: {
    id: string;
    settings?: RetrySettings;
  }): Promise<GetActionsSummaryResponseDTO> {
    return (
      await this.client.get<GetActionsSummaryResponseDTO>(
        `${request.id}/actions/summary`,
        {},
        {
          bearerToken: await getAuthToken(),
          settings: request.settings,
        },
      )
    ).data;
  }

  async getActionLayersByRequestId(request: {
    id: string;
    settings?: RetrySettings;
  }): Promise<GetActionLayersResponseDTO> {
    return (
      await this.client.get<GetActionLayersResponseDTO>(
        `${request.id}/actions/layers`,
        {},
        {
          bearerToken: await getAuthToken(),
          settings: request.settings,
        },
      )
    ).data;
  }

  async getActionById(request: { id: string; actionId: string; settings?: RetrySettings }): Promise<ActionDTO> {
    return (
      await this.client.get<ActionDTO>(
        `${request.id}/actions/${request.actionId}`,
        {},
        {
          bearerToken: await getAuthToken(),
          settings: request.settings,
        },
      )
    ).data;
  }

  async postRunAction(request: { id: string; actionId: string; settings?: RetrySettings }): Promise<ActionDTO> {
    return (
      await this.client.post<object, ActionDTO>(
        `${request.id}/actions/${request.actionId}/run`,
        {},
        {
          bearerToken: await getAuthToken(),
          settings: request.settings,
        },
      )
    ).data;
  }

  async getReports(request: { id: string; settings?: RetrySettings }): Promise<ReportDTO[]> {
    return (
      await this.client.get<ReportDTO[]>(
        `${request.id}/reports`,
        {},
        {
          bearerToken: await getAuthToken(),
          settings: request.settings,
        },
      )
    ).data;
  }

  async delete(request: { id: string; settings?: RetrySettings }): Promise<void> {
    await this.client.delete<void>(`${request.id}`, {}, {
      bearerToken: (await getAuthToken()) ?? undefined,
      settings: request.settings,
    });
  }

  async postExtract(request: { id: string; settings?: RetrySettings }): Promise<void> {
    const SCRIPTS_BASE_URL = import.meta.env.VITE_SCRIPTS_BASE_URL || '';
    await axios.post(
      `${SCRIPTS_BASE_URL}/webhooks/requests/${request.id}/extract`,
      {},
      {
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      },
    );
  }
}
